import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { WordpressLogo } from '../components/WordpressLogo';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: email, 2: otp
  const [resendTimer, setResendTimer] = useState(0);
  const resendTimeout = 30; // seconds
  const navigate = useNavigate();
  const [isLoggingIn, setIsLoggingIn] = useState(false); // New state for login button loading
  const [isVerifying, setIsVerifying] = useState(false); // New state for verify button loading
  const [isResending, setIsResending] = useState(false); // New state for resend button loading

  // Check if user is already authenticated
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/'); // Redirect to home if already authenticated
    }
  }, [navigate]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      setError(t('login.errors.emailRequired'));
      return;
    }

    setError('');
    setIsLoggingIn(true);

    try {
      const cardId = localStorage.getItem('id'); // get card_id from localStorage

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/check-email`,
        {
          email,
          card_id: cardId || null, // send null if no card_id
        }
      );

      if (response.status === 201) {
        setStep(2);
        setResendTimer(resendTimeout);
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          setError(t('login.errors.emailNotFound'));
        } else if (error.response.status === 400) {
          // Laravel sends this when card_id and email do not match
          setError(t('login.errors.emailCardMismatch'));
        } else {
          setError(t('login.errors.serverError'));
        }
      } else {
        setError(t('login.errors.serverError'));
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer === 0) {
      setIsResending(true);
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/resend-otp`, { email });
        if (response.status === 201) {
          setResendTimer(resendTimeout);
          setError('');
        }
      } catch (error) {
        setError(t('login.errors.resendFailed'));
      } finally {
        setIsResending(false);
      }
    }
  };

  const handleChangeEmail = () => {
    setStep(1);
    setOtp('');
    setError('');
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError(t('login.errors.otpRequired'));
    } else {
      setError('');
      setIsVerifying(true);
      try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/verify-otp`, { email, otp });
        if (response.status === 200) {
          localStorage.setItem('userEmail', email);
          localStorage.setItem('token', response.data.token);
          navigate('/dashboard');
        }
      } catch (err) {
        setError(t('login.errors.invalidOtp'));
      } finally {
        setIsVerifying(false);
      }
    }
  };

  React.useEffect(() => {
    let timer;
    if (step === 2 && resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [step, resendTimer]);

  return (
    <div className="flex pt-8 md:pt-0 md:items-center justify-center min-h-full md:min-h-screen bg-background px-2">
      <form className="md:bg-white p-6 sm:p-8 md:rounded md:shadow-md w-full max-w-md" onSubmit={step === 1 ? handleEmailSubmit : handleOtpSubmit}>
        <div className="flex justify-center mb-6">
          <WordpressLogo />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-6">{t('login.title')}</h2>
        {error && <div className="mb-4 text-danger">{error}</div>}
        {step === 1 && (
          <div className="mb-4">
            <label className="block mb-1 text-foreground text-base sm:text-sm">{t('login.emailLabel')}</label>
            <input type="email" placeholder={t('login.emailPlaceholder')} className="w-full border rounded px-4 py-3 text-base sm:text-sm focus:outline-primary" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="username" />
          </div>
        )}
        {step === 2 && (
          <div className="mb-4">
            <label className="block mb-1 text-foreground text-base sm:text-sm">{t('login.otpLabel')}</label>
            <input type="text" placeholder={t('login.otpPlaceholder')} className="w-full border rounded px-4 py-3 text-base sm:text-sm focus:outline-primary" value={otp} onChange={e => setOtp(e.target.value)} required autoComplete="one-time-code" />
            <div className="text-xs text-foreground/60 mt-2">{t('login.otpHint')}</div>
            <div className="flex items-center justify-between mt-2 gap-2">
              <button type="button" className="text-primary underline text-xs" onClick={handleChangeEmail} disabled={isLoggingIn || isVerifying || isResending}>{t('login.changeEmailButton')}</button>
              <button
                type="button"
                className={`px-4 py-2 rounded text-xs font-semibold transition-colors duration-100 active:scale-95 focus:outline-none
                  ${resendTimer === 0 && !isResending ? 'btn-secondary' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                onClick={handleResendOtp}
                disabled={resendTimer !== 0 || isResending}
              >
                {isResending ? t('login.resendingOtp') : (resendTimer === 0 ? t('login.resendOtpButton') : t('login.resendOtpButtonWithTimer', { count: resendTimer }))}
              </button>
            </div>
          </div>
        )}
        <button type="submit" className="w-full btn-primary" disabled={step === 1 ? isLoggingIn : isVerifying}>
          {step === 1 ? (isLoggingIn ? t('login.loggingIn') : t('login.loginButton')) : (isVerifying ? t('login.verifyingOtp') : t('login.verifyOtpButton'))}
        </button>
      </form>
    </div>
  );
};

export default Login;
